/**
 * Firestore event collection routing for dev vs prod.
 *
 * Write collection (REACT_APP_SERVER_LOCATION): creates/updates/deletes.
 * Read collection (REACT_APP_EVENTS_READ_COLLECTION): optional overlay for
 * read-only Rentman events from production while developing against `development`.
 */

function readEnv(key) {
  if (typeof window !== 'undefined' && window.electronAPI?.env?.[key]) {
    return window.electronAPI.env[key];
  }
  return process.env[key];
}

/** Collection used for all event writes (create/update/delete). */
export function getEventWriteCollection() {
  return readEnv('REACT_APP_SERVER_LOCATION') || 'production';
}

/**
 * Optional collection for read overlay. Returns null when unset or same as write
 * (single-collection mode).
 */
export function getEventReadCollection() {
  if (readEnv('REACT_APP_USE_FIREBASE_EMULATORS') === 'true') {
    return null;
  }
  const write = getEventWriteCollection();
  const read = readEnv('REACT_APP_EVENTS_READ_COLLECTION');
  if (!read || read === write) {
    return null;
  }
  return read;
}

export function normalizeEventDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'string') return new Date(val);
  if (typeof val.toDate === 'function') return val.toDate();
  if (val._seconds) return new Date(val._seconds * 1000);
  return val;
}

/** Map a Firestore event document into the calendar event shape. */
export function mapFirestoreEventDoc(docSnap) {
  const data = docSnap.data();
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    start: normalizeEventDate(data.start),
    end: normalizeEventDate(data.end),
    allDay: data.allDay,
    status: data.status,
    statusCode: data.statusCode ?? null,
    statusName: data.statusName ?? null,
    color: data.color,
    resource: data.resource,
    editable: data.editable,
    busy: data.busy,
    createdAt: normalizeEventDate(data.createdAt),
    updatedAt: normalizeEventDate(data.updatedAt),
    visibleInCalendar: data.visibleInCalendar,
    rentmanEvent: data.rentmanEvent,
    subprojectStatus: data.subprojectStatus,
    parentProject: data.parentProject,
    prepAndRetur: data.prepAndRetur,
    planStart: data.planStart,
    usageStart: data.usageStart,
    usageEnd: data.usageEnd,
    planEnd: data.planEnd,
    vacation: data.vacation ?? false,
    sick: data.sick ?? false,
    sickChild: data.sickChild ?? false,
  };
}

/** Keep events whose range overlaps [startOfYear, endOfYear]. */
export function filterEventsOverlappingYear(events, startOfYear, endOfYear) {
  return events.filter((event) => {
    if (!event.start || !event.end) return false;
    const eventStart = event.start instanceof Date ? event.start : new Date(event.start);
    const eventEnd = event.end instanceof Date ? event.end : new Date(event.end);
    return eventStart <= endOfYear && eventEnd >= startOfYear;
  });
}

/**
 * Merge read-overlay Rentman events with writable dev events.
 * When useReadOverlay is false, returns writeEvents only.
 */
export function mergeCalendarEvents(readEvents, writeEvents, useReadOverlay) {
  if (!useReadOverlay) {
    return writeEvents || [];
  }
  const prodRentman = (readEvents || []).filter((e) => e.rentmanEvent === true);
  const devEvents = (writeEvents || []).filter((e) => !e.rentmanEvent);
  return [...prodRentman, ...devEvents];
}
